import { QueryObserverResult, RefetchOptions, RefetchQueryFilters, useQueryClient } from '@tanstack/react-query';

import { useQuery } from '@hooks/reactQuery';

import { getChangeMonitoringActive } from '@api/manageNetworkElementConfigurations';

import { extractQueryParams } from '@utils/common';

import { IItemsResponse } from '@interfaces/common/response';
import { IChangeMonitoringDTO } from '@interfaces/networkElementConfigurations/dto/networkElementConfigurationsDTO';
import { IChangeMonitoring } from '@interfaces/networkElementConfigurations/networkElementConfigurations';

import { EUserRoles } from '@enums/common';

import { refetchOptions } from '@constants/common';
import { NETWORK_ELEMENT_CHANGE_MONITORING } from '@constants/queryKeys';

interface IUseQueryChangeMonitoringReturn {
	isLoadingChangeMonitoringActive: false | true;
	changeMonitoringActive: IChangeMonitoring[];
	countOfRecords: number;
	invalidateChangeMonitoringActive: () => void;
	refetchChangeMonitoring: <TPageData>(options?: (RefetchOptions & RefetchQueryFilters<TPageData>))
		=> Promise<QueryObserverResult<IItemsResponse<IChangeMonitoring>, any>>;
}

export const useQueryChangeMonitoringActive = (
	params: IChangeMonitoringDTO,
): IUseQueryChangeMonitoringReturn => {
	const queryClient = useQueryClient();

	const {
		data,
		isInitialLoading,
		refetch,
	} = useQuery({
		queryKey: [
			NETWORK_ELEMENT_CHANGE_MONITORING,
			params,
		],
		queryFn: extractQueryParams(getChangeMonitoringActive),
		enabled: [params?.isActive, !!params?.networkElementId],
		allowedRoles: [
			EUserRoles.networkOperatorCmModuleEditor,
			EUserRoles.networkOperatorCmModuleAdmin,
			EUserRoles.networkOperatorCmModuleViewer,
		],
		...refetchOptions,
	});

	const invalidateQuery = () => {
		return [queryClient.invalidateQueries([NETWORK_ELEMENT_CHANGE_MONITORING])];
	};

	return {
		isLoadingChangeMonitoringActive: isInitialLoading,
		changeMonitoringActive: data?.items || [],
		countOfRecords: data?.count || 0,
		invalidateChangeMonitoringActive: invalidateQuery,
		refetchChangeMonitoring: refetch,
	};
};
